import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { getConfig, getCriancaAtiva } from "../../db/queries";
import { elegiveisEscolhaCorporal } from "../domain";
import { listarAtividades, registrarEscolhaCrianca } from "../queries";
import { PassoParticipacao, type AtividadePsicomotora } from "../types";
import { AtividadeFoto } from "../ui/AtividadeFoto";

/**
 * P0.3 — modo escolha corporal da criança. Tela cheia, mídia grande, alvos enormes.
 * Mostra 2–3 opções elegíveis e SEGURAS agora (status não `sensivel`); a criança toca
 * UMA → celebração visual curta ("combinado!"), a escolhida ganha destaque e as outras
 * desbotam (nunca apagadas). Gera um momento origem=crianca (pendente). "trocar"
 * recomeça. Diferente do Compasso, a atividade acontece no mundo físico — por isso a
 * celebração é só visual e o aviso de segurança aparece junto.
 *
 * O passo registrado é "exploração" (participação positiva da escolha); o cuidador
 * confirma e ajusta o passo observado depois. A sessão reseta a cada entrada.
 */
const PASSO_DA_ESCOLHA = PassoParticipacao.Exploracao;

export function ModoEscolhaCorporal() {
  const nav = useNavigate();
  const location = useLocation();
  const crianca = useLiveQuery(getCriancaAtiva);
  const cfg = useLiveQuery(getConfig);
  const atividades = useLiveQuery(() => listarAtividades()) ?? [];

  const [escolhido, setEscolhido] = useState<string | null>(null);
  const registrouRef = useRef(false);

  const qtd = Math.max(2, Math.min(3, cfg?.gingado_escolha_qtd ?? 2));

  // sorteia um subconjunto estável por entrada (location.key) das elegíveis
  const opcoes = useMemo(() => {
    const elegiveis = elegiveisEscolhaCorporal(atividades);
    return elegiveis
      .map((a) => ({ a, r: Math.random() }))
      .sort((x, y) => x.r - y.r)
      .map((x) => x.a)
      .slice(0, qtd);
    // location.key entra de propósito pra re-sortear a cada nova entrada
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atividades.length, location.key, qtd]);

  const escolhida = opcoes.find((a) => a.id === escolhido) ?? null;

  // reseta a sessão a cada entrada nova
  useEffect(() => {
    setEscolhido(null);
    registrouRef.current = false;
  }, [location.key]);

  async function escolher(ativ: AtividadePsicomotora) {
    if (escolhido || registrouRef.current || !crianca) return;
    registrouRef.current = true;
    setEscolhido(ativ.id);
    await registrarEscolhaCrianca(ativ.id, crianca.id, PASSO_DA_ESCOLHA);
  }

  function trocar() {
    setEscolhido(null);
    registrouRef.current = false;
  }

  // sem atividades elegíveis
  if (opcoes.length === 0) {
    return (
      <div className="crianca">
        <div className="crianca-combinado">
          <span className="emoji" aria-hidden>
            🤸
          </span>
          <span style={{ color: "var(--text-muted)", fontSize: "1.1rem" }}>
            marque atividades como favoritas ou em exploração pra criança escolher.
          </span>
          <button className="btn btn-primario btn-grande" onClick={() => nav("/gingado/biblioteca")}>
            abrir biblioteca
          </button>
          <button className="btn btn-grande" onClick={() => nav("/gingado")}>
            voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="crianca">
      <button className="btn-voltar" onClick={() => nav("/gingado")} aria-label="sair do modo criança">
        ←
      </button>
      <div className="crianca-prompt">{escolhido ? "combinado! 🎉" : "qual brincadeira você quer?"}</div>

      <div className="crianca-opcoes grade">
        {opcoes.map((a) => {
          const sel = escolhido === a.id;
          const desbotado = escolhido !== null && !sel;
          return (
            <button
              key={a.id}
              className={`crianca-card${sel ? " escolhido" : ""}${desbotado ? " desbotado" : ""}`}
              aria-pressed={sel}
              onClick={() => escolher(a)}
            >
              <AtividadeFoto midias={a.midias} nome={a.nome} tipo={a.tipo} />
              <span className="rotulo">
                {sel ? "✓ " : ""}
                {a.nome}
              </span>
            </button>
          );
        })}
      </div>

      {/* segurança da atividade escolhida — visível antes de ir brincar (P0.8) */}
      {escolhida?.seguranca && (
        <div className="callout-seguranca" style={{ margin: "0 0 var(--s3)" }}>
          <span aria-hidden>⚠️</span> {escolhida.seguranca}
        </div>
      )}

      <div className="crianca-barra">
        {escolhido ? (
          <>
            <button className="btn crescer btn-grande" onClick={trocar}>
              trocar
            </button>
            <button
              className="btn btn-primario crescer btn-grande"
              onClick={() => nav("/gingado")}
            >
              pronto
            </button>
          </>
        ) : (
          <button className="btn crescer btn-grande" onClick={() => nav("/gingado")}>
            sair
          </button>
        )}
      </div>
    </div>
  );
}
